import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import useInfiniteScroll from "react-infinite-scroll-hook";
import FeedbackTopHeader from "../../../newComponents/listings/top-listing-headers/FeedbackTopHeader";
import FeedbackCardItem from "./FeedbackCardItem";
import AddFeedbackForm from "./AddFeedbackForm";
import { PlatformService } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import { UserLoginData } from "../../common/charts/Enums";
import SimpleSpinner from "../../loader";
import { GetImages } from "../../../utils/GetImages";
import EmptyState from "../../../newComponents/emptystate";
import apiHelper from "../../../utils/apiHelper";

// Permission support
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { buildFeedbackTypeFilterOptions } from "./feedbackCountHelpers";
import { ALL_FILTER_KEY, resolveAllFilterCount, useAggregateFilterCounts } from "../../../hooks/useAggregateFilterCounts";
import { getApiFeedbackOrganizationlistCounts } from "../../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../../utils/dynamicLinq";

const FeedbackComponent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const check = usePermissions();
  const canWrite = check.canWrite("Feedback");
  const organizationId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || "0", 10);

  const searchOnlyFilter = useMemo(() => {
    if (!debouncedSearchTerm) return null;
    return `FeedbackDescription.ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`;
  }, [debouncedSearchTerm]);

  const fullyQualifiedFilters = useMemo(() => {
    const filters: string[] = [];

    if (activeFilter !== ALL_FILTER_KEY) {
      filters.push(`FeedbackTypeId == ${activeFilter}`);
    }

    if (searchOnlyFilter) {
      filters.push(searchOnlyFilter);
    }

    return filters.length ? filters.join(" && ") : null;
  }, [activeFilter, searchOnlyFilter]);

  const {
    rows,
    loading,
    hasMore,
    currentPage,
    fetchData,
    rowCount,
  } = infiniteScrollHook({
    httpFetchDataFn: (
      orgId: number,
      pageSize?: number,
      pageNumber?: number,
      skipValue?: number,
      filterEntityTypeId?: any,
      filterEntityIds?: any,
      filters?: string,
      sorts?: string,
      ...rest: any[]
    ) =>
      PlatformService.getApiFeedbackOrganizationlist(
        orgId,
        pageSize,
        pageNumber,
        skipValue,
        filterEntityTypeId,
        filterEntityIds,
        filters,
        sorts,
        ...rest
      ),
    FullyQualifiedFilters: fullyQualifiedFilters,
    pageSize: 10,
    FullyQualifiedSorts: "Created desc",
  });

  const fetchCounts = useCallback(async (filter: string | null) => {
    if (!organizationId) return undefined;

    const res = await apiHelper(
      getApiFeedbackOrganizationlistCounts,
      { showNotification: false },
      organizationId,
      1,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      undefined,
    );

    return res?.data?.aggregateValues ?? undefined;
  }, [organizationId]);

  const { aggregateValues: storedAggregates, refreshCounts, getVisibleOptions } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `feedback:${organizationId}`,
  });

  const totalCount = useMemo(
    () => resolveAllFilterCount(storedAggregates),
    [storedAggregates],
  );

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, value: ALL_FILTER_KEY, count: totalCount },
        ...buildFeedbackTypeFilterOptions(storedAggregates),
      ]) as { title: string; value: string; count: number }[],
    [getVisibleOptions, storedAggregates, totalCount],
  );

  const loadMoreRecords = useCallback(() => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  }, [loading, hasMore, fetchData, currentPage]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const refreshListAndCounts = useCallback(() => {
    fetchData(1);
    refreshCounts();
  }, [fetchData, refreshCounts]);

  const handleEdit = (item: any) => {
    setSelectedFeedback(item);
    setModalOpen(true);
  };

  const handleAddClick = useCallback(() => {
    setSelectedFeedback(null);
    setModalOpen(true);
  }, []);

  const isEmpty = rows.length === 0 && !loading;

  return (
    <PermissionGuard permission="Feedback" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="content-wrapper container-fluid">
        <FeedbackTopHeader
          PageHeading="Feedback"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          filterOptions={visibleFilterOptions}
          onAddClick={canWrite ? handleAddClick : undefined}
        />

        <div className="infinite-scroll-container">
          {loading && rows.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
              <SimpleSpinner myClassName="Icon50" />
            </div>
          ) : isEmpty ? (
            <EmptyState
              icon={GetImages.emptyStateFeedback}
              heading="No Feedback Found"
              isButton={false}
              isBorder
            />
          ) : (
            <>
              {rows.map((item: any) => (
                <FeedbackCardItem
                  key={item.id}
                  listingData={item}
                  onEdit={() => handleEdit(item)}
                />
              ))}

              {(loading || hasMore) && (
                <div ref={sentryRef} className="loading-trigger">
                  {loading && (
                    <SimpleSpinner myClassName="Icon40" />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {isModalOpen && (
          <AddFeedbackForm
            isModalOpen={isModalOpen}
            setModalOpen={setModalOpen}
            editData={selectedFeedback}
            fetchData={refreshListAndCounts}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default FeedbackComponent;
